import {
  Smartphone, ScanLine, Hand, Layers, Search, Wrench, CheckCircle2, ShieldCheck, Zap, Clock, MapPin, Star,
} from 'lucide-react';
import Seo from '../../components/Seo';
import ServicePageLayout from '../../components/services/ServicePageLayout';
import { ServiceSection, ServiceFAQ, RelatedServices } from '../../components/services/ServiceUI';
import {
  ServiceHeroPro, ProofStrip, DiagnosisList, ProcessTimeline, PriceCardPro, AvisStrip, LocalCTA,
} from '../../components/services/elevated';
import { REVIEWS, RATING, REVIEW_COUNT } from '../../data/reviews';

const SLUG = '/reparation-ecran-iphone-montpellier';

const faq = [
  { q: "Combien coûte un écran d'iPhone 11 à Montpellier ?", a: "Le tarif dépend de la qualité de pièce choisie ; pour un iPhone 11, le remplacement d'écran démarre à 65 €. Nous vous remettons un devis gratuit immédiat en boutique." },
  { q: "La réparation d'écran fait-elle perdre les données ?", a: "Non, le remplacement d'écran n'affecte pas le contenu de votre iPhone." },
  { q: "Le Face ID fonctionnera-t-il après le changement d'écran ?", a: "Oui. Nous préservons les composants liés au Face ID lors du remplacement et testons systématiquement son fonctionnement." },
  { q: "Combien de temps faut-il ?", a: "En moyenne 30 minutes, sans rendez-vous." },
];

const cas = [
  { icon: Smartphone, title: "Vitre cassée", desc: "Vitre fissurée mais affichage encore fonctionnel." },
  { icon: ScanLine, title: "Écran noir ou lignes", desc: "Écran noir, lignes colorées ou zones mortes." },
  { icon: Hand, title: "Tactile capricieux", desc: "Ne répond plus ou réagit seul (effet « ghost touch »)." },
  { icon: Layers, title: "Écran décollé", desc: "Décollement ou bombement de l'écran." },
];

const steps = [
  { icon: Search, title: "Diagnostic gratuit", sub: "Devis précis, sans engagement." },
  { icon: Wrench, title: "Remplacement 30 min", sub: "Écran changé en boutique." },
  { icon: CheckCircle2, title: "Test complet", sub: "Tactile, luminosité, Face ID." },
  { icon: ShieldCheck, title: "Garantie 6 mois", sub: "Pièce et main d'œuvre." },
];

const proofItems = [
  { icon: Zap, label: 'Réparé en 30 min' },
  { icon: ShieldCheck, label: 'Garantie 6 mois' },
  { icon: Clock, label: 'Sans rendez-vous' },
  { icon: Star, label: '4,7/5 · 579 avis' },
  { icon: MapPin, label: 'Centre-ville Montpellier' },
];

const ReparationEcranIphone = () => (
  <ServicePageLayout trail={[{ name: 'Accueil', to: '/' }, { name: 'Réparation iPhone', to: '/reparation-iphone-montpellier' }, { name: "Réparation d'écran" }]}>
    <Seo
      title="Réparation Écran iPhone Montpellier — dès 45€ | AlloRép."
      description="Réparation écran iPhone à Montpellier en 30 min : vitre cassée, tactile, dalle. Devis gratuit, pièces qualité, garantie 6 mois."
      canonicalPath={SLUG}
      service={{
        name: "Réparation d'écran iPhone à Montpellier",
        serviceType: "Remplacement d'écran iPhone",
        description: "Remplacement d'écran iPhone (vitre, dalle, tactile) à Montpellier en 30 minutes, garantie 6 mois.",
        offers: { '@type': 'Offer', priceCurrency: 'EUR', price: '45', description: "À partir de 45 € selon le modèle d'iPhone" },
      }}
      faq={faq}
      breadcrumb={[
        { name: 'Accueil', path: '/' },
        { name: 'Réparation iPhone', path: '/reparation-iphone-montpellier' },
        { name: "Réparation d'écran iPhone à Montpellier", path: SLUG },
      ]}
    />

    <ServiceHeroPro
      eyebrow="Écran iPhone — Montpellier"
      h1Before="Réparation d'"
      h1Highlight="écran iPhone"
      h1After=" à Montpellier"
      intro="Vitre fissurée, tactile qui ne répond plus, lignes ou taches sur l'affichage ? AlloRéparations remplace l'écran de votre iPhone à Montpellier en 30 minutes, sans rendez-vous, avec garantie 6 mois. Le devis est gratuit et l'iPhone 11 — le modèle le plus concerné — est traité le jour même."
      image={{ src: '/iphones/hub-iphone-11.jpg', alt: "Réparation d'écran iPhone à Montpellier — iPhone 11" }}
      rating={{ value: RATING, count: REVIEW_COUNT }}
    />

    <ProofStrip items={proofItems} />

    <DiagnosisList title="Les cas que nous traitons" items={cas} />

    <ServiceSection title="Écran original, compatible ou OLED : que choisir ?">
      <p>
        Selon votre budget et votre modèle, nous proposons plusieurs qualités de pièces. Sur les iPhone à dalle
        OLED, un écran compatible de qualité reproduit l'affichage d'origine à moindre coût, tandis qu'un écran
        d'origine garantit la colorimétrie exacte. Nous vous expliquons la différence avant l'intervention pour
        que vous décidiez en connaissance de cause.
      </p>
    </ServiceSection>

    <ProcessTimeline title="Déroulé de la réparation" steps={steps} />

    <PriceCardPro
      eyebrow="Tarifs par modèle"
      title="Prix d'une réparation d'écran iPhone"
      columns={["Modèle d'iPhone", 'Délai', 'Prix TTC']}
      rows={[
        ['iPhone 11 / 11 Pro', '30 min', '65–99 €'],
        ['iPhone 12 / 13', '30 min', '95–125 €'],
        ['iPhone 14 / 15', '30 min', '120–195 €'],
        ['iPhone X / XR / XS', '30 min', '55–69 €'],
        ['iPhone 8 et antérieurs', '30 min', '45 €'],
      ]}
    />

    <AvisStrip rating={RATING} count={REVIEW_COUNT} reviews={REVIEWS.slice(0, 3)} />

    <ServiceFAQ faq={faq} />

    <LocalCTA heading="Écran d'iPhone cassé ? Réparé en 30 minutes." sub="Diagnostic et devis gratuits, sans rendez-vous, garantie 6 mois." />

    <RelatedServices
      links={[
        { label: 'Réparation iPhone (toutes pannes)', to: '/reparation-iphone-montpellier', desc: 'La page pilier iPhone.' },
        { label: 'Changement de batterie iPhone', to: '/changement-batterie-iphone-montpellier', desc: 'Autonomie restaurée en 30 min.' },
      ]}
    />
  </ServicePageLayout>
);

export default ReparationEcranIphone;
